import type { ReactNode } from 'react';

import { Box, Stack, Typography } from '@mui/material';

export interface PageContainerProps {
  /** Page title (rendered as an `h1`). */
  title?: ReactNode;
  /** Supporting subtitle/description. */
  subtitle?: ReactNode;
  /** Right-aligned header actions (buttons, menus, etc.). */
  actions?: ReactNode;
  /** Constrain the content width. */
  maxWidth?: number | string;
  /** Remove the default page padding. */
  disableGutters?: boolean;
  children: ReactNode;
}

/**
 * Consistent page wrapper: centres content, applies responsive padding, and
 * renders an optional title / subtitle / actions header. Business-agnostic.
 */
export function PageContainer({
  title,
  subtitle,
  actions,
  maxWidth = 1200,
  disableGutters = false,
  children,
}: PageContainerProps) {
  const hasHeader = Boolean(title || subtitle || actions);

  return (
    <Box
      sx={{
        width: '100%',
        mx: 'auto',
        maxWidth,
        px: disableGutters ? 0 : { xs: 2, md: 3 },
        py: disableGutters ? 0 : { xs: 2, md: 3 },
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        boxSizing: 'border-box',
      }}
    >
      {hasHeader ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            {title ? (
              <Typography variant="h4" component="h1">
                {title}
              </Typography>
            ) : null}
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {actions ? <Box sx={{ flexShrink: 0 }}>{actions}</Box> : null}
        </Stack>
      ) : null}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>{children}</Box>
    </Box>
  );
}
